import {
  Card,
  CardBody,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from 'reactstrap';

import Breadcrumbs from '../../../coreComponents/breadcrumbs';

import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LeadersTabContent from './leadersTabContent';
import CommentFileModal, { PreviewItem } from '../../commetFileModal';

import { validateImages } from '../../../lib/filesUtils';
import { ServerFile, usePreviews } from '../../../hooks/usePreviews';

import Swal from 'sweetalert2';

import {
  AllDBUsersLeaderboard,
  StatsStatusDetails,
  StatusHistoryItem,
} from '../../../types/Tables';
import { getCrmControlHistoryTableColumns } from '../../../data/formsAndTable/tables/dataTables/DataSource';
import { getConditionals } from '../../../constants';

import { CustomLoader } from '../../uiKits/customLoader/CustomLoader';
import { useLeaderBoardData } from './hooks/useLeaderBoardData';
import { useLeaderBoardActions } from './hooks/useLeaderActions';
import { StatusEditorModal } from '../../uiKits/modal/StatusEditorModal/StatusEditorModal';
import { StatusHistoryModal } from '../../uiKits/modal/statusHistory/StatusHistoryModal';
import { useGetAllCoursesQuery } from '../../../reduxToolkit/reducers/usersSlice';
import { PlainCourse } from '../../../types/Courses';
import { co } from '@fullcalendar/core/internal-common';

const LeaderBoardPageContainer = () => {
  const { t } = useTranslation();

  const [openNewStatusModal, setOpenNewStatusModal] = useState(false);
  const [basicTab, setBasicTab] = useState<{ tabId: string; courseId: number } | null>(null);
  const [statsId, setStatsId] = useState<number | null>(null);

  const [previewFiles, setPreviewFiles] = useState<ServerFile[]>([]);
  const [showAddDetailsModal, setShowAddDetailsModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const commentRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const [comment, setComment] = useState('');
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<{
    id: number;
    statusName: string;
    colorCode: string;
  } | null>(null);
  const [newStatusName, setNewStatusName] = useState<string>('');

  const [isEditingStatus, setIsEditingStatus] = useState<boolean>(false);
  const [editStatus, setEditStatus] = useState<string>('');

  const [lastAddedStatus, setLastAddedStatus] = useState<{
    id: number;
    statusName: string;
    colorCode: string;
  } | null>(null);
  const [chosenColor, setChosenColor] = useState<string>('#ffffff');
  const [colorInputValue, setColorInputValue] = useState<string>('#ffffff');
  const [validationError, setValidationError] = useState<string>('');

  const [isOpenHistoryModal, setIsOpenHistoryModal] = useState(false);
  const [historyStatusDetails, setHistoryStatusDetails] = useState<StatusHistoryItem[]>([]);

  const {
    statsStatuses,
    usersLeadersOnStats,
    sort,
    setSort,
    sortKey,
    setSortKey,
    page,
    setPage,
    perPage,
    setPerPage,
    search,
    setSearch,
    totalRows,

    setTotalRows,
    filters,
    setFilters,
    appliedFilters,
    setAppliedFilters,
    applyFiltersCounter,
    setApplyFiltersCounter,
    setDebouncedSearch,
  } = useLeaderBoardData(basicTab);
  const { data: coursesList } = useGetAllCoursesQuery({
    page: 1,
    perPage: 1000,
    search: '',
    sortKey: 'id',
    sortDir: 'asc',
  });
  const { submitComment, saveNewStatus, deleteStatus, editCurrentStatus } = useLeaderBoardActions();
  const makeAbs = useCallback(
    (rel?: string) =>
      rel
        ? `${process.env.REACT_APP_BACKEND_STATIC}/leaders/${rel}`
        : 'data:image/gif;base64,R0lG...',
    [],
  );
  const { previews, addFiles, removePreview, clearNonExisting, newFiles } = usePreviews(
    previewFiles,
    makeAbs,
  );

  const dropDownContentForModal = (() => {
    const base = statsStatuses ?? [];
    if (!lastAddedStatus) return base;
    return [lastAddedStatus, ...base.filter((s: any) => s.id !== lastAddedStatus.id)];
  })();

  const viewerTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const coursesTabs = useMemo(() => {
    return (
      coursesList?.map((course: PlainCourse, i: number) => ({
        id: course.id,
        title: `${course.name.length > 20 ? course.name.substring(0, 20) + '...' : course.name}`,
        fullTitle: course.name, 
        tabId: `${i + 1}`,
      })) ?? []
    );
  }, [coursesList]);

  useEffect(() => {
    const storedTabs = localStorage.getItem('stored-leaders-tabs');
    console.log('coursesTabs', coursesTabs);
    if (storedTabs) {
      const parsedTabs = JSON.parse(storedTabs);
      if (coursesTabs.some((tab: { id: number }) => tab.id === parsedTabs.courseId))
        setBasicTab(parsedTabs);
    } else if (coursesTabs.length) {
      setBasicTab(coursesTabs[0]);
    }
  }, [coursesTabs]);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    console.log('usersLeadersOnStats', usersLeadersOnStats);
    // If appliedFilters is set or we have stats results, prefer the stats pagination total.
    if (appliedFilters && usersLeadersOnStats && usersLeadersOnStats.pagination) {
      setTotalRows(usersLeadersOnStats.pagination.total ?? 0);
    } else {
      setTotalRows(usersLeadersOnStats?.pagination?.total ?? 0);
    }
    // Fallback to the general usersLeaders total
  }, [usersLeadersOnStats, appliedFilters]);

  const handleModalOpen = (id: number) => {
    setStatsId(id);
    setPreviewFiles([]);
    const selectedUser =
      usersLeadersOnStats?.data.find((item: AllDBUsersLeaderboard) => item.id === id) ?? null;
    const statusFromDB = selectedUser
      ? {
          id: selectedUser.statusId,
          statusName: selectedUser.statusName,
          colorCode: selectedUser.statusColorCode,
        }
      : null;
    // If a status was added in this session prefer that as preselected, otherwise use DB status
    setSelectedStatus(lastAddedStatus ?? statusFromDB);
    setShowAddDetailsModal(true);
  };

  const handleFilesSelected: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    const { valid, errors } = validateImages(files, t);
    setUploadErrors(errors);
    if (valid.length) addFiles(valid);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemovePreview = (p: PreviewItem) => {
    removePreview(p);
  };

  const handleSubmitComment = async () => {
    if (!statsId) return;

    submitComment({
      statsId: statsId,
      selectedStatus: selectedStatus,
      newFiles: newFiles,
      leaderId: statsId,
      comment: comment,
    });
    try {
      setSubmitting(true);
      setShowAddDetailsModal(false);
      // await commentHandler(fd).unwrap();
      clearNonExisting();
      setSubmitting(false);
      // await new Promise((r) => setTimeout(r, 500));
      // Swal.fire({
      //   title: t('leads.notifications.successTitle'),
      //   text: t('leads.notifications.successText'),
      //   icon: 'success',
      // });
    } catch (error) {
      setSubmitting(false);
      console.log('error', error);
      Swal.fire({
        title: t('leads.notifications.errorCommentTitle'),
        text: t('leads.notifications.errorUpdateText'),
        icon: 'error',
      });
    } finally {
      setComment('');
      setPreviewFiles([]);
      setSelectedStatus(null);
      setNewStatusName('');
      setLastAddedStatus(null);
    }
  };

  const handleOpenNewStatusModal = () => {
    setOpenNewStatusModal(true);
  };

  const handleSaveNewStatus = async () => {
    setValidationError('');
    const name = newStatusName?.trim();
    if (!name) {
      setValidationError(t('global.selector.nameRequired') || 'Name is required');
      return;
    }
    if (name.length > 30) {
      setValidationError(t('exams.totalLeaderBoard.filteredTable.nameTooLong') || 'Name too long');
      return;
    }
    const color = colorInputValue || chosenColor || '#ffffff';
    const isHex = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(color);
    if (!isHex) {
      setValidationError(t('exams.totalLeaderBoard.filteredTable.invalidColor') || 'Invalid color');
      return;
    }

    try {
      setOpenNewStatusModal(false);
      await new Promise((r) => setTimeout(r, 500));
      const res = await saveNewStatus({ name, colorCode: color });
      const added = {
        id: res.statusId,
        statusName: res.statusName,
        colorCode: res.colorCode,
      };

      // setSelectedStatus(null);
      setLastAddedStatus(added);
      setNewStatusName('');
    } catch (err) {
      console.error('failed to add status', err);
    }
  };
  const handleHistoryOpen = (id: number) => {
    const selectedUser =
      usersLeadersOnStats?.data.find((item: AllDBUsersLeaderboard) => item.id === id) ?? null;
    if (!selectedUser) return;
    const userStatusDetails = selectedUser.statusDetails || [];
    const history =
      [...userStatusDetails].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ) ?? [];
    const formattedHistory = history.map((item: StatsStatusDetails, i: number) => ({
      action: item.statusId,
      timestamp: item.createdAt,
      oldComment: item.comments[0]?.text || null,
      actionName: item.statusName,
      files:
        item.comments[0]?.files.map((file) => {
          return {
            ...file,
            url: `exam-stats/${file.url}`,
          };
        }) || null,
      colorCode: item.statusColor,
      actionNamePrev: history[i + 1]?.statusName || null,
      prevColorCode: history[i + 1]?.statusColor || null,
    }));
    setHistoryStatusDetails(formattedHistory);
    setIsOpenHistoryModal(true);
  };

  const handleApplyFilters = () => {
    // snapshot current filters and bump counter to trigger query
    setAppliedFilters(filters);
    setApplyFiltersCounter((c) => c + 1);
  };
  const modalData = {
    isOpen: openNewStatusModal,
    center: true,
    toggler: () => setOpenNewStatusModal(!openNewStatusModal),
  };

  const hideCommentModal = () => {
    setShowAddDetailsModal(false);
  };
  const handleStatusEdit = async (
    id: number,
    statusName: string,
    colorCode: string,
    source?: string,
  ) => {
    console.log('edit status', { id, statusName, colorCode, source });
    setIsEditingStatus(true);
    setEditStatus(statusName || '');
    setChosenColor(colorCode || '#ffffff');
    setColorInputValue(colorCode || '#ffffff');
    hideCommentModal();
    await new Promise((r) => setTimeout(r, 1000));
    setOpenNewStatusModal(true);
  };

  const handleConfirmedStatusDelete = async (id: number) => {
    try {
      await deleteStatus({
        statusId: id,
      });
      setChosenColor('#ffffff');
      setColorInputValue('#ffffff');
      setOpenNewStatusModal(false);
    } catch (error) {
      console.log('error', error);
      setChosenColor('#ffffff');
      setColorInputValue('#ffffff');
      setOpenNewStatusModal(false);
    }
  };

  const handleStatusDelete = async (id: number, source?: string) => {
    hideCommentModal();
    await new Promise((r) => setTimeout(r, 1000));
    try {
      const result = await Swal.fire({
        title: t('global.statusMutationsText.titleDelete'),
        text: t('global.statusMutationsText.textDelete'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: t('global.yes'),
        cancelButtonText: t('global.no'),
      });
      if (result.isConfirmed) {
        await handleConfirmedStatusDelete(id);
      } else {
        await new Promise((r) => setTimeout(r, 300));
        setShowAddDetailsModal(true);
      }
    } catch (error) {
      await Swal.fire({
        title: t('global.statusMutationsText.errorTitleDelete'),
        text: t('global.statusMutationsText.errorTextDelete'),
        icon: 'error',
      });
    }
  };

  const handleSaveEditStatus = async () => {
    setValidationError('');
    const name = editStatus?.trim() || '';
    const onSuccessCb = () => {
      setIsEditingStatus(false);
      setEditStatus('');
      setChosenColor('#ffffff');
      setColorInputValue('#ffffff');
      setOpenNewStatusModal(false);
      new Promise((r) => setTimeout(r, 300)).then(() => {
        setShowAddDetailsModal(true);
      });
    };
    const onErrorCb = () => {
      setIsEditingStatus(false);
      setEditStatus('');
      setChosenColor('#ffffff');
      setColorInputValue('#ffffff');
      setOpenNewStatusModal(false);
      new Promise((r) => setTimeout(r, 300)).then(() => {
        setShowAddDetailsModal(true);
      });
    };
    try {
      const result = await Swal.fire({
        title: t('global.statusMutationsText.titleEdit'),
        text: t('global.statusMutationsText.textEdit'),
        icon: 'warning',
        confirmButtonText: t('global.ok'),
        cancelButtonText: t('global.cancel'),
        showCancelButton: true,
      });
      if (result.isConfirmed) {
        // slight delay so modal toggles feel smooth
        await editCurrentStatus({
          statusId: selectedStatus ? Number(selectedStatus.id) : 0,
          name,
          colorCode: colorInputValue,
          onSuccessCb,
          onErrorCb,
        });
      } else {
        await new Promise((r) => setTimeout(r, 500));

        setIsEditingStatus(false);
        setEditStatus('');
        setChosenColor('#ffffff');
        setColorInputValue('#ffffff');
        setOpenNewStatusModal(false);
        setShowAddDetailsModal(true);
      }
    } catch (err) {
      console.error('failed to edit status', err);
    }
  };

  return (
    <>
      <Breadcrumbs mainTitle={t('exams.totalLeaderBoard.title')} />
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <Nav tabs className="simple-wrapper mt-3">
                  {coursesTabs.map(
                    (item: { id: number; title: string; fullTitle: string }, index: number) => (
                      <NavItem key={index}>
                        <NavLink
                          tag="button"
                          type="button"
                          className={`txt-info !h4 f-w-500 ${basicTab?.tabId === `${index + 1}` ? 'active' : ''}`}
                          onClick={() => {
                            setBasicTab({
                              tabId: `${index + 1}`,
                              courseId: item.id,
                            });
                            localStorage.setItem(
                              'stored-leaders-tabs',
                              JSON.stringify({
                                tabId: `${index + 1}`,
                                courseId: item.id,
                              }),
                            );
                          }}
                          title={item.fullTitle}
                        >
                          {item.title}
                        </NavLink>
                      </NavItem>
                    ),
                  )}
                </Nav>
                <TabContent activeTab={basicTab?.tabId}>
                  {!coursesTabs && <CustomLoader />}
                  {coursesTabs.map(
                    (item: { id: number; title: string; fullTitle: string }, index: number) => (
                      <TabPane
                        className={`fade ${basicTab?.tabId === `${index + 1}` ? 'show' : ''}`}
                        tabId={`${index + 1}`}
                        key={index + 1}
                      >
                        <LeadersTabContent
                          tabItem={item}
                          statItems={usersLeadersOnStats?.data ?? []}
                          perPage={perPage}
                          totalRows={totalRows}
                          setPage={setPage}
                          setPerPage={setPerPage}
                          setSort={setSort}
                          setSortKey={setSortKey}
                          search={search}
                          setSearch={setSearch}
                          handleModalOpen={handleModalOpen}
                          handleHistoryOpen={handleHistoryOpen}
                          filters={filters}
                          setFilters={setFilters}
                          statuses={
                            statsStatuses &&
                            statsStatuses.map((s: { id: number; statusName: string }) => ({
                              value: s.id,
                              label: t(`global.selectorStatus.${s.statusName}`, {
                                defaultValue: s.statusName,
                              }),
                              colorCode:
                                (s as any).colorCode ?? (s as any).statusColorCode ?? '#dddddd',
                            }))
                          }
                          applyFilters={handleApplyFilters}
                          conditions={getConditionals(t).map((cond) => ({
                            value: cond.value,
                            label: cond.label,
                            editLabel: null,
                            deleteHandler: null,
                          }))}
                        />
                      </TabPane>
                    ),
                  )}
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <CommentFileModal
          isOpen={showAddDetailsModal}
          onClose={() => setShowAddDetailsModal(false)}
          labels={{
            title: t('leads.commentForm.label'),
            placeholder: t('leads.commentForm.placeholder'),
            ok: t('global.ok'),
            saving: t('global.saving'),
            cancel: t('global.cancel'),
            remove: t('common.remove'),
            examHandled: t('leads.examHandled'),
            yes: t('exams.yes'),
            no: t('exams.no'),
            selectStatus: t('global.selector.selectStatus'),
          }}
          commentRef={commentRef as React.RefObject<HTMLTextAreaElement>}
          comment={comment}
          onCommentChange={setComment}
          hasFileInput={true}
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
          onFilesSelected={handleFilesSelected}
          previews={previews}
          onRemovePreview={handleRemovePreview}
          uploadErrors={uploadErrors}
          submitting={submitting}
          onSubmit={handleSubmitComment}
          filesRequired={false}
          dropDown={true}
          dropDownContent={dropDownContentForModal}
          dropDownPreSelected={
            selectedStatus
              ? {
                  id: selectedStatus.id,
                  statusName: selectedStatus.statusName,
                  colorCode: selectedStatus.colorCode,
                }
              : null
          }
          addOptionHandler={handleOpenNewStatusModal}
          dropDownSelectHandler={setSelectedStatus}
          isClearable={false}
          commentRequired={true}
          handleStatusEdit={handleStatusEdit}
          handleStatusDelete={handleStatusDelete}
          // dropDownAddValueHandler={handleDropDownAddValue}
        />

        <StatusEditorModal
          modalData={modalData}
          selectedStatus={selectedStatus}
          onSave={isEditingStatus ? handleSaveEditStatus : handleSaveNewStatus}
          onCancel={() => setOpenNewStatusModal(false)}
          name={isEditingStatus ? editStatus : newStatusName}
          setName={isEditingStatus ? setEditStatus : setNewStatusName}
          color={colorInputValue}
          setColor={(color: string) => {
            setColorInputValue(color);
            setChosenColor(color);
          }}
          validationError={validationError}
          crmControlStatuses={statsStatuses}
        />
      </Container>
      <StatusHistoryModal
        isOpen={isOpenHistoryModal}
        close={() => setIsOpenHistoryModal(false)}
        size="xl"
        tableData={historyStatusDetails}
        columns={getCrmControlHistoryTableColumns(t, viewerTZ)}
        defaultSortFieldId="timestamp"
        defaultSortAsc={false}
        hasFilter={false}
      />
    </>
  );
};

export default LeaderBoardPageContainer;
